import userOutlined from '@iconify/icons-ant-design/user-outlined';
import { InlineIcon } from '@iconify/react/dist/iconify';
import React, { useMemo } from 'react';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';
import { cssVars } from '../../../app/styles';
import { dayjs } from '../../../utils/dayjs';
import { MONTH_WIDTH_PX, REVIEWER_IMAGE_BASE_STYLE } from '../../constants';
import { Rating } from './rating';

const ReviewContainer = styled.div`
  padding: 25px 50px;
  display: flex;
  flex-direction: column;
  width: ${MONTH_WIDTH_PX}px;
  gap: 10px;
`;

const ReviewHeading = styled.h5``;

const ReviewBody = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const ReviewerImage = styled.img`
  ${REVIEWER_IMAGE_BASE_STYLE}
  border-radius: 60px;
`;

const ReviewerName = styled.div`
  font-weight: bold;
`;

const ReviewerDetails = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const ReviewerBasicDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReviewDate = styled.div``;

const EmptyReviewerImageContainer = styled.div`
  ${REVIEWER_IMAGE_BASE_STYLE}
  border-radius: 60px;
  background: ${cssVars.propertyHistoryColors.reviewerAvatarPlaceholderBg};
  font-size: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${cssVars.propertyHistoryColors.reviewerAvatarPlaceholderFg};
`;

const EmptyReviewerImage = () => (
  <EmptyReviewerImageContainer>
    <InlineIcon icon={userOutlined} />
  </EmptyReviewerImageContainer>
);

const REVIEW_DATE_FORMAT = 'MMMM DD, YYYY';

function ReviewerImageSection({ reviewerProfileImage }) {
  if (!reviewerProfileImage) {
    return <EmptyReviewerImage />;
  }
  return <ReviewerImage src={reviewerProfileImage} />;
}

function useSanitizedHtml(rawHtml) {
  return useMemo(
    () =>
      sanitizeHtml(rawHtml, {
        allowedTags: [],
      }),
    [rawHtml]
  );
}

export function Review({ data, style }) {
  const {
    headline: rawHeadline,
    body: rawBody,
    rating,
    reviewerProfileImage,
    reviewerNickname,
    createdTs,
  } = data;

  const hasSeparateHeadline = rawHeadline && rawHeadline !== rawBody;

  const body = useSanitizedHtml(rawBody);
  const headline = useSanitizedHtml(rawHeadline);

  const formattedReviewDate = useMemo(
    () => dayjs.unix(createdTs).format(REVIEW_DATE_FORMAT),
    [createdTs]
  );

  return (
    <ReviewContainer style={style}>
      <ReviewerDetails>
        <ReviewerImageSection reviewerProfileImage={reviewerProfileImage} />
        <ReviewerBasicDetails>
          <ReviewerName>{reviewerNickname}</ReviewerName>
          <ReviewDate>{formattedReviewDate}</ReviewDate>
          <Rating value={rating} />
        </ReviewerBasicDetails>
      </ReviewerDetails>
      {hasSeparateHeadline && <ReviewHeading>{headline}</ReviewHeading>}
      <ReviewBody>{body}</ReviewBody>
    </ReviewContainer>
  );
}
